import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Download, Trash2, ChevronLeft, ChevronRight, X, ImageIcon, AlertCircle, Link, Eye, Video, Play, Film, Tag, Globe, Lock } from 'lucide-react';
import ConfirmModal from '../ConfirmModal';

const DashboardPhotos = () => {
  const [photos, setPhotos] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterType, setFilterType] = useState('all'); // all | image | video
  const [filterCat, setFilterCat] = useState('all');
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [lightbox, setLightbox] = useState({ open: false, index: 0 });
  const touchStart = useRef(null);

  useEffect(() => {
    Promise.all([
      axios.get('/api/user/photos'),
      axios.get('/api/categories'),
    ]).then(([mRes, cRes]) => {
      setPhotos(mRes.data);
      setCategories(cRes.data);
    }).catch(() => setError('Failed to load photos'))
      .finally(() => setLoading(false));
  }, []);

  const catMap = {};
  categories.forEach((c) => { catMap[c.id] = c; });

  const filtered = photos.filter((m) => {
    if (filterType !== 'all' && m.type !== filterType) return false;
    if (filterCat !== 'all' && m.categoryId !== filterCat) return false;
    return true;
  });

  const handleDelete = async (id) => {
    setConfirmDelete(null);
    setDeletingId(id);
    try {
      await axios.delete(`/api/photos/${id}`);
      toast.success('Deleted');
      setPhotos((prev) => prev.filter((m) => m.id !== id));
      setLightbox((prev) => ({ ...prev, open: false }));
    } catch (err) {
      toast.error(err.response?.data?.error || 'Delete failed');
    } finally { setDeletingId(null); }
  };

  const handleDownload = (item) => {
    const a = document.createElement('a');
    a.href = `/api/photos/${item.id}/download`;
    a.download = item.originalName || item.filename;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };

  const handleCopyLink = async (item) => {
    try { await navigator.clipboard.writeText(window.location.origin + item.url); toast.success('Link copied'); }
    catch { toast.error('Copy failed'); }
  };

  const closeLightbox = useCallback(() => setLightbox({ open: false, index: 0 }), []);
  const goNext = useCallback(() => setLightbox((p) => ({ open: true, index: (p.index + 1) % filtered.length })), [filtered.length]);
  const goPrev = useCallback(() => setLightbox((p) => ({ open: true, index: (p.index - 1 + filtered.length) % filtered.length })), [filtered.length]);

  useEffect(() => {
    if (!lightbox.open) return;
    const h = (e) => { if (e.key === 'Escape') closeLightbox(); if (e.key === 'ArrowRight') goNext(); if (e.key === 'ArrowLeft') goPrev(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [lightbox.open, closeLightbox, goNext, goPrev]);

  useEffect(() => {
    document.body.style.overflow = lightbox.open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [lightbox.open]);

  const handleTouchStart = (e) => { touchStart.current = e.touches[0].clientX; };
  const handleTouchEnd = (e) => {
    if (touchStart.current === null) return;
    const diff = touchStart.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) diff > 0 ? goNext() : goPrev();
    touchStart.current = null;
  };

  const updateCategory = async (itemId, categoryId) => {
    try {
      await axios.put(`/api/photos/${itemId}`, { categoryId });
      setPhotos((prev) => prev.map((m) => m.id === itemId ? { ...m, categoryId } : m));
      toast.success('Category updated');
    } catch { toast.error('Failed to update'); }
  };

  const toggleVisibility = async (itemId, currentVisibility) => {
    const newVis = currentVisibility === 'public' ? 'private' : 'public';
    try {
      await axios.put(`/api/photos/${itemId}`, { visibility: newVis });
      setPhotos((prev) => prev.map((m) => m.id === itemId ? { ...m, visibility: newVis } : m));
      toast.success(`Set to ${newVis}`);
    } catch { toast.error('Failed to update'); }
  };

  if (loading) {
    return (
      <div className="space-y-4 animate-fade-in">
        <div className="skeleton h-7 w-32 rounded-lg" />
        <div className="flex gap-2"><div className="skeleton h-8 w-20 rounded-full" /><div className="skeleton h-8 w-20 rounded-full" /><div className="skeleton h-8 w-20 rounded-full" /></div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {Array.from({ length: 8 }).map((_, i) => <div key={i} className="aspect-square skeleton rounded-xl" />)}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-3 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm animate-fade-in">
        <AlertCircle size={16} className="shrink-0" /><span>{error}</span>
        <button onClick={() => { setError(''); setLoading(true); window.location.reload(); }} className="ml-auto text-xs font-medium text-red-600 hover:text-red-800 cursor-pointer">Retry</button>
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-heading font-bold text-lg text-foreground">Media Library</h2>
        <span className="badge-outline">{photos.length} total · {filtered.length} shown</span>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        {/* Type filter */}
        <div className="flex items-center bg-card border border-border rounded-xl overflow-hidden">
          {[{ v: 'all', l: 'All' }, { v: 'image', l: 'Photos', icon: ImageIcon }, { v: 'video', l: 'Videos', icon: Video }].map((f) => (
            <button key={f.v} onClick={() => setFilterType(f.v)}
              className={`flex items-center gap-1 px-3 py-2 text-xs font-semibold transition-colors cursor-pointer ${
                filterType === f.v ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}>
              {f.icon && <f.icon size={12} />}{f.l}
            </button>
          ))}
        </div>

        {/* Category filter */}
        <select value={filterCat} onChange={(e) => setFilterCat(e.target.value)}
          className="px-3 py-2 bg-card border border-border rounded-xl text-xs font-semibold text-muted-foreground cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary/20">
          <option value="all">Semua kategori</option>
          {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>

      {/* Empty */}
      {photos.length === 0 ? (
        <div className="card py-16 text-center">
          <div className="w-14 h-14 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <ImageIcon size={24} className="text-primary" />
          </div>
          <h3 className="font-heading font-semibold text-sm text-foreground mb-1">No media yet</h3>
          <p className="text-muted-foreground text-xs max-w-xs mx-auto">Upload from the Upload page.</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="card py-12 text-center">
          <p className="text-muted-foreground text-sm">No media matches current filters</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 stagger">
          {filtered.map((item, i) => (
            <div key={item.id} className="group relative aspect-square rounded-xl overflow-hidden bg-muted shadow-xs hover:shadow-lg transition-all duration-300 hover:scale-[1.02]">
              {item.type === 'video' ? (
                <div className="absolute inset-0">
                  <video src={item.url} className="w-full h-full object-cover" preload="metadata" muted />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-10 h-10 bg-black/40 backdrop-blur-sm rounded-full flex items-center justify-center border border-white/20">
                      <Play size={16} className="text-white ml-0.5" fill="white" />
                    </div>
                  </div>
                </div>
              ) : (
                <button onClick={() => setLightbox({ open: true, index: i })} className="absolute inset-0 cursor-pointer">
                  <img src={item.url} alt={item.filename} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" loading="lazy"
                    onError={(e) => { e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect fill="%23F7F3FD" width="100" height="100"/></svg>'; }} />
                </button>
              )}

              {/* Type badge */}
              {item.type === 'video' && (
                <div className="absolute top-2 left-2 z-10">
                  <span className="inline-flex items-center gap-1 bg-black/50 backdrop-blur-sm text-white text-[10px] font-semibold px-1.5 py-0.5 rounded-md">
                    <Film size={9} /> VID
                  </span>
                </div>
              )}

              {/* Visibility badge */}
              <div className="absolute top-2 right-2 z-10">
                <button
                  onClick={(e) => { e.stopPropagation(); toggleVisibility(item.id, item.visibility || 'public'); }}
                  className={`inline-flex items-center gap-1 backdrop-blur-sm text-[10px] font-semibold px-1.5 py-0.5 rounded-md cursor-pointer transition-colors ${
                    item.visibility === 'private'
                      ? 'bg-amber-500/80 text-white hover:bg-amber-600/80'
                      : 'bg-emerald-500/80 text-white hover:bg-emerald-600/80'
                  }`}
                  title={`Click to set ${item.visibility === 'private' ? 'public' : 'private'}`}
                >
                  {item.visibility === 'private' ? <Lock size={9} /> : <Globe size={9} />}
                  {item.visibility === 'private' ? 'Private' : 'Public'}
                </button>
              </div>

              {/* Hover overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200" />

              {/* Desktop hover actions */}
              <div className="absolute bottom-0 left-0 right-0 p-3 flex items-end justify-between opacity-0 group-hover:opacity-100 transition-all duration-200 translate-y-2 group-hover:translate-y-0">
                <span className="text-xs text-white font-medium truncate max-w-[60%] drop-shadow">{item.originalName || item.filename}</span>
                <div className="flex items-center gap-1">
                  <button onClick={() => handleDownload(item)} className="w-7 h-7 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full flex items-center justify-center text-white transition-colors cursor-pointer" aria-label="Download"><Download size={12} /></button>
                  <button onClick={() => handleCopyLink(item)} className="w-7 h-7 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full flex items-center justify-center text-white transition-colors cursor-pointer" aria-label="Copy link"><Link size={12} /></button>
                  {item.type === 'image' && (
                    <button onClick={() => setLightbox({ open: true, index: i })} className="w-7 h-7 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full flex items-center justify-center text-white transition-colors cursor-pointer" aria-label="View"><Eye size={12} /></button>
                  )}
                  <button onClick={() => setConfirmDelete(item)} disabled={deletingId === item.id}
                    className="w-7 h-7 bg-white/20 hover:bg-red-500/80 backdrop-blur-sm rounded-full flex items-center justify-center text-white transition-colors cursor-pointer disabled:opacity-50" aria-label="Delete">
                    {deletingId === item.id ? <span className="w-3 h-3 border border-white/30 border-t-white rounded-full animate-spin" /> : <Trash2 size={12} />}
                  </button>
                </div>
              </div>

              {/* Mobile always-visible actions */}
              <div className="sm:hidden absolute top-2 right-2 flex gap-1">
                <button onClick={() => handleDownload(item)} className="w-7 h-7 bg-black/40 backdrop-blur-sm rounded-full flex items-center justify-center text-white cursor-pointer"><Download size={12} /></button>
                <button onClick={() => setConfirmDelete(item)} className="w-7 h-7 bg-black/40 backdrop-blur-sm rounded-full flex items-center justify-center text-white cursor-pointer"><Trash2 size={12} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      <ConfirmModal
        open={!!confirmDelete}
        title={`Delete ${confirmDelete?.type === 'video' ? 'video' : 'photo'}?`}
        message={`"${confirmDelete?.originalName || confirmDelete?.filename}" will be permanently deleted.`}
        confirmLabel="Delete"
        onConfirm={() => confirmDelete && handleDelete(confirmDelete.id)}
        onCancel={() => setConfirmDelete(null)}
      />

      {/* Lightbox */}
      {lightbox.open && filtered.length > 0 && filtered[lightbox.index]?.type === 'image' && (
        <div className="fixed inset-0 z-[100] flex flex-col bg-black/95 animate-fade-in" onClick={closeLightbox}
          onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
          <div className="flex items-center justify-between px-4 sm:px-6 py-3 shrink-0">
            <span className="text-white/60 text-sm font-mono">{lightbox.index + 1} / {filtered.length}</span>
            <div className="flex items-center gap-1">
              <button onClick={(e) => { e.stopPropagation(); handleDownload(filtered[lightbox.index]); }}
                className="p-2.5 text-white/60 hover:text-white hover:bg-white/10 rounded-xl transition-colors cursor-pointer" aria-label="Download"><Download size={18} /></button>
              <button onClick={(e) => { e.stopPropagation(); handleCopyLink(filtered[lightbox.index]); }}
                className="p-2.5 text-white/60 hover:text-white hover:bg-white/10 rounded-xl transition-colors cursor-pointer" aria-label="Copy link"><Link size={18} /></button>
              <button onClick={(e) => { e.stopPropagation(); setConfirmDelete(filtered[lightbox.index]); }}
                className="p-2.5 text-white/60 hover:text-red-400 hover:bg-white/10 rounded-xl transition-colors cursor-pointer" aria-label="Delete"><Trash2 size={18} /></button>
              <button onClick={closeLightbox} className="p-2.5 text-white/60 hover:text-white hover:bg-white/10 rounded-xl transition-colors cursor-pointer" aria-label="Close"><X size={18} /></button>
            </div>
          </div>
          <div className="flex-1 flex items-center justify-center px-4 min-h-0 relative">
            {filtered.length > 1 && (
              <button onClick={(e) => { e.stopPropagation(); goPrev(); }}
                className="absolute left-2 sm:left-6 p-3 text-white/60 hover:text-white hover:bg-white/10 rounded-full transition-colors cursor-pointer z-10 backdrop-blur-sm" aria-label="Previous"><ChevronLeft size={24} /></button>
            )}
            <img src={filtered[lightbox.index].url} alt={filtered[lightbox.index].filename}
              className="max-w-full max-h-full object-contain rounded-lg select-none shadow-2xl animate-scale-in" onClick={(e) => e.stopPropagation()} draggable={false} />
            {filtered.length > 1 && (
              <button onClick={(e) => { e.stopPropagation(); goNext(); }}
                className="absolute right-2 sm:right-6 p-3 text-white/60 hover:text-white hover:bg-white/10 rounded-full transition-colors cursor-pointer z-10 backdrop-blur-sm" aria-label="Next"><ChevronRight size={24} /></button>
            )}
          </div>
          <div className="px-4 py-3 text-center shrink-0">
            <p className="text-white text-sm font-medium truncate">{filtered[lightbox.index].originalName || filtered[lightbox.index].filename}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardPhotos;
